import React from 'react'
import { Tooltip } from '@surecompanies/core_components'
import { TableOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'

import { ADJ_LINK_KEYS, BENEFITS_URL_KEYS } from '../BenefitsTable/helpers'
import { replaceOwnedQuery } from '../replaceOwnedQuery'
import styles from './ReviewQueue.module.scss'

/**
 * Jump to the Benefits tab with THIS row's benefit searched — the row's year of
 * context, which neither queue table has room to show.
 *
 * Two things it must get right, both learned from the surrounding code:
 *
 * 1. **It carries `year`.** Benefit search is year-scoped (`BenefitsTable`
 *    resolves a deep link against the LINKED period's year, not the table's
 *    current one), so a jump without it lands on whatever year the operator
 *    last looked at and finds nothing.
 * 2. **It owns every Benefits key, and writes only two of them.** A search jump
 *    depends on the list actually showing a row, so a stale `hasIssue`,
 *    `carrier` or `emptyInMonth` — the recipient's or the sender's — would land
 *    the operator on an empty table with no hint why. Same rule `rqOpen`
 *    follows: a deep link overrides the rest of its namespace.
 * 3. **It clears the `adj*` one-shots too.** Those are read once by
 *    `BenefitsTable` at mount and open the Adjust Payment modal on the benefit
 *    they name. An unconsumed pair — a link whose panel never mounted, or a
 *    failed load — would otherwise survive this jump and pop a modal for a
 *    DIFFERENT benefit the moment we land, burying the one the operator asked
 *    for. Exactly the hazard `rqOpen` has at the page level, and the same fix.
 *
 * Note this is WIDER ownership than the `handleOpenAdjust` jump in
 * TransactionDetailsPanel, which owns only `['tab', ...ADJ_LINK_KEYS]`. That one
 * opens a modal directly and does not care what the list behind it shows.
 *
 * Not gated on write access: reading a benefit's year is a read.
 */
const JumpToBenefitButton: React.FC<{
  benefitId: string
  /** Any date inside the coverage year — a period start, or a transaction date. */
  date: string | null
}> = ({ benefitId, date }) => {
  const parsed = date ? dayjs(date) : null
  const year = parsed?.isValid() ? parsed.year() : null

  return (
    <Tooltip title="Show this benefit on the Benefits tab">
      <button
        type="button"
        aria-label="Show this benefit on the Benefits tab"
        className={styles.copyButton}
        onClick={() =>
          replaceOwnedQuery(['tab', ...BENEFITS_URL_KEYS, ...ADJ_LINK_KEYS], {
            tab: 'benefits',
            q: benefitId,
            // Omitted rather than guessed when the row's date is unusable:
            // BenefitsTable falls back to its own default year, which is a
            // better answer than a year derived from nothing.
            ...(year === null ? {} : { year: String(year) }),
          })
        }
      >
        <TableOutlined />
      </button>
    </Tooltip>
  )
}

export default JumpToBenefitButton
